/**
 * Font preference resolver — pure function, no DOM, no I/O.
 *
 * Mirrors the logic that Swift's `FontResolver` runs at runtime so both
 * platforms produce identical results for the same `FontPreferences` blob.
 */

import type { FontPreferences, FontRegion } from '@caliu/types'
import { DEFAULT_FONT_PREFERENCES, FONT_REGIONS } from '@caliu/types'

export interface CatalogEntry {
  /** The CSS `font-family` value to use directly in a `font-family:` declaration. */
  family: string
}

export type ResolvedFonts = Record<FontRegion, string>

/**
 * Resolve every region to a concrete font-family string.
 *
 * Falls back to the default chrome font when an id is missing from the
 * catalog — only happens if a user's stored prefs reference a deleted font.
 */
export function resolveFontPreferences(
  prefs: FontPreferences,
  catalog: Readonly<Record<string, CatalogEntry>>,
): ResolvedFonts {
  const fallbackId = DEFAULT_FONT_PREFERENCES.regions.chrome
  const fallbackFamily = catalog[fallbackId]?.family ?? 'sans-serif'

  const result = {} as ResolvedFonts
  for (const region of FONT_REGIONS) {
    const id = prefs.regions[region]
    result[region] = catalog[id]?.family ?? fallbackFamily
  }
  return result
}

/**
 * Sanitize a stored `FontPreferences` blob: fills in missing keys with
 * defaults so an old localStorage value never crashes the resolver.
 */
export function normalizeFontPreferences(
  prefs: Partial<FontPreferences> | null | undefined,
): FontPreferences {
  if (!prefs) return clonePreferences(DEFAULT_FONT_PREFERENCES)

  const regions = { ...DEFAULT_FONT_PREFERENCES.regions }
  if (prefs.regions) {
    for (const region of FONT_REGIONS) {
      const candidate = (prefs.regions as Partial<Record<FontRegion, unknown>>)[region]
      if (typeof candidate === 'string' && candidate.length > 0) {
        regions[region] = candidate
      }
    }
  }

  return { regions }
}

function clonePreferences(prefs: FontPreferences): FontPreferences {
  return {
    regions: { ...prefs.regions },
  }
}
